/*
  Friction event hooks — Growth + Proof Engine
  Lightweight emitters that POST to /api/activation/track.
  Designed to be ZERO-impact on perf. No throttling-by-design beyond batching.

  Usage:
    1. Mount <RageClickWatcher /> once at the app root → emits `rage_click`.
    2. Mount <NavLoopWatcher /> once at the app root → emits `nav_loop`.
    3. useChatExitNoSend() in ChatPage → emits on unmount if 0 user messages were sent.
    4. useUploadAbandoned() in DocumentsPage → wraps an upload sheet open/close pair.
    5. useOnboardingAbandoned() in OnboardingPage → emits if user idles for 10+ min.
*/
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { track } from '../services/activation';

// ─── Rage click watcher — global ────────────────────────────────
// 3+ clicks within 1.0s on the same screen-position cluster (≤40px radius)
export const RageClickWatcher = () => {
  useEffect(() => {
    const buf = [];
    const onClick = (e) => {
      const now = Date.now();
      // Drop entries older than 1s
      while (buf.length && now - buf[0].t > 1000) buf.shift();
      buf.push({ t: now, x: e.clientX, y: e.clientY, target: (e.target?.tagName || '').toLowerCase() });
      if (buf.length < 3) return;
      // Check that all are within ~40px of the first
      const first = buf[0];
      const tight = buf.every((p) => Math.hypot(p.x - first.x, p.y - first.y) <= 40);
      if (tight) {
        try {
          track('rage_click', {
            x: first.x,
            y: first.y,
            target: first.target,
            path: window.location.pathname,
            count: buf.length,
          });
        } catch { /* noop */ }
        buf.length = 0; // reset so we don't double-fire
      }
    };
    window.addEventListener('click', onClick, { passive: true });
    return () => window.removeEventListener('click', onClick);
  }, []);
  return null;
};

// ─── Nav loop watcher — global ──────────────────────────────────
// Same path visited 3+ times in 30s → emit `nav_loop`.
export const NavLoopWatcher = () => {
  const location = useLocation();
  const buf = useRef([]);
  useEffect(() => {
    const now = Date.now();
    const path = location.pathname;
    const arr = buf.current;
    // Drop entries older than 30s
    while (arr.length && now - arr[0].t > 30000) arr.shift();
    arr.push({ t: now, p: path });
    // Count how many of the last entries match this path
    const same = arr.filter((e) => e.p === path);
    if (same.length >= 3) {
      try { track('nav_loop', { path, count: same.length, window_ms: 30000 }); } catch {}
      // Reset so we don't refire continuously
      buf.current = [];
    }
  }, [location.pathname]);
  return null;
};

// ─── Chat exit without sending — page hook ──────────────────────
export function useChatExitNoSend(getSentCountFn) {
  // getSentCountFn: () => number of user messages sent in this chat session
  useEffect(() => {
    return () => {
      try {
        const sent = (typeof getSentCountFn === 'function') ? getSentCountFn() : 0;
        if (!sent || sent <= 0) {
          track('chat_exit_no_send', { path: window.location.pathname });
        }
      } catch { /* noop */ }
    };
    // intentionally empty deps — fire ONLY on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ─── Upload abandoned — sheet open/close hook ───────────────────
// Call hook returns a tuple [markOpened, markSubmitted, markClosed].
export function useUploadAbandonedTracker() {
  const stateRef = useRef({ openedAt: 0, submitted: false });
  return [
    function markOpened() {
      stateRef.current = { openedAt: Date.now(), submitted: false };
      try { track('upload_opened', {}); } catch {}
    },
    function markSubmitted() {
      stateRef.current.submitted = true;
    },
    function markClosed() {
      const s = stateRef.current;
      if (s.openedAt && !s.submitted) {
        const ms_open = Date.now() - s.openedAt;
        try { track('upload_abandoned', { ms_open }); } catch {}
      }
      stateRef.current = { openedAt: 0, submitted: false };
    },
  ];
}

// ─── Onboarding abandoned — idle / unmount hook ─────────────────
// Fire if user idles 10+ min during onboarding without finishing.
export function useOnboardingAbandoned({ stepIndex, completed, totalSteps }) {
  const lastInteractionRef = useRef(Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    const onActivity = () => { lastInteractionRef.current = Date.now(); };
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('click', onActivity, { passive: true });
    window.addEventListener('touchstart', onActivity, { passive: true });
    return () => {
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('click', onActivity);
      window.removeEventListener('touchstart', onActivity);
    };
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      if (firedRef.current || completed) return;
      const idle = Date.now() - lastInteractionRef.current;
      if (idle > 10 * 60 * 1000) {
        try { track('onboarding_abandoned', { step: stepIndex, total: totalSteps, idle_ms: idle, reason: 'idle_10m' }); } catch {}
        firedRef.current = true;
      }
    }, 60 * 1000);
    return () => clearInterval(t);
  }, [stepIndex, completed, totalSteps]);

  // Fire on unmount if user navigated away mid-flow
  useEffect(() => {
    return () => {
      if (firedRef.current || completed) return;
      try { track('onboarding_abandoned', { step: stepIndex, total: totalSteps, reason: 'unmount' }); } catch {}
      firedRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
